import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/api";
import { BarChart3, RefreshCw, Eye, Users, Rocket, Loader2 } from "lucide-react";

const WINDOWS = [
  { label: "7d", days: 7 },
  { label: "30d", days: 30 },
  { label: "90d", days: 90 },
];

function StatCard({ label, value, sub, icon: Icon, testid }) {
  return (
    <div className="vbs-card" style={{ padding: 16, display: "flex", flexDirection: "column", gap: 4 }} data-testid={testid}>
      <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 10, letterSpacing: 1.5, color: "var(--gold-dim)", textTransform: "uppercase" }}>
        {Icon && <Icon size={11} />} {label}
      </div>
      <div style={{ fontFamily: "var(--font-display)", fontSize: 26, color: "var(--bright)", fontWeight: 700 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "var(--muted)" }}>{sub}</div>}
    </div>
  );
}

/** Tiny inline-SVG line chart — no chart lib needed. */
function DailyChart({ data }) {
  const { points, maxV, w, h } = useMemo(() => {
    const w = 760, h = 130, pad = 8;
    if (!data?.length) return { points: "", maxV: 0, w, h };
    const maxV = Math.max(1, ...data.map((d) => d.views));
    const step = (w - pad * 2) / Math.max(1, data.length - 1);
    const pts = data.map((d, i) => {
      const x = pad + i * step;
      const y = h - pad - (d.views / maxV) * (h - pad * 2);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    });
    return { points: pts.join(" "), maxV, w, h };
  }, [data]);

  if (!data?.length) {
    return <div style={{ padding: 24, textAlign: "center", color: "var(--muted)", fontSize: 12 }}>No data in this window yet — visit any page to seed.</div>;
  }
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" preserveAspectRatio="none" style={{ display: "block" }} data-testid="analytics-daily-chart">
      <defs>
        <linearGradient id="vbsLineFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#C4952A" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#C4952A" stopOpacity="0" />
        </linearGradient>
      </defs>
      <polyline points={`8,${h - 8} ${points} ${w - 8},${h - 8}`} fill="url(#vbsLineFill)" stroke="none" />
      <polyline points={points} fill="none" stroke="#C4952A" strokeWidth="1.6" />
      <text x="10" y="14" fill="#888070" fontSize="9">max {maxV}</text>
    </svg>
  );
}

function TopTable({ title, rows, keyLabel = "Page", testid, renderKey }) {
  return (
    <div className="vbs-card" style={{ padding: 16 }} data-testid={testid}>
      <div style={{ fontSize: 11, color: "var(--gold-dim)", letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 10 }}>{title}</div>
      {!rows?.length ? (
        <div style={{ fontSize: 12, color: "var(--muted)" }}>—</div>
      ) : (
        <table className="vbs-table">
          <thead>
            <tr><th>{keyLabel}</th><th style={{ textAlign: "right" }}>Hits</th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key}>
                <td style={{ fontSize: 12, color: "var(--bright)", maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.key}>{renderKey ? renderKey(r.key) : r.key}</td>
                <td style={{ textAlign: "right", color: "var(--gold)", fontWeight: 600, fontSize: 12 }}>{r.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ISO-3166 country name lookup + emoji flag. We avoid pulling a 50KB
// country library — for the top-N admin widget a small in-memory map of
// the ~60 most common buyer countries plus a flag-from-iso emoji helper
// is plenty. Unknown codes fall through to "(unknown)".
const COUNTRY_NAMES = {
  US: "United States", CA: "Canada", GB: "United Kingdom", AU: "Australia", NZ: "New Zealand",
  IE: "Ireland", DE: "Germany", FR: "France", ES: "Spain", IT: "Italy", PT: "Portugal",
  NL: "Netherlands", BE: "Belgium", SE: "Sweden", NO: "Norway", DK: "Denmark", FI: "Finland",
  CH: "Switzerland", AT: "Austria", PL: "Poland", CZ: "Czechia", GR: "Greece", HU: "Hungary",
  RO: "Romania", BG: "Bulgaria", UA: "Ukraine", RU: "Russia", TR: "Turkey", IL: "Israel",
  AE: "UAE", SA: "Saudi Arabia", EG: "Egypt", ZA: "South Africa", NG: "Nigeria", KE: "Kenya",
  GH: "Ghana", MA: "Morocco", BR: "Brazil", MX: "Mexico", AR: "Argentina", CL: "Chile",
  CO: "Colombia", PE: "Peru", VE: "Venezuela", IN: "India", PK: "Pakistan", BD: "Bangladesh",
  LK: "Sri Lanka", PH: "Philippines", ID: "Indonesia", MY: "Malaysia", SG: "Singapore",
  TH: "Thailand", VN: "Vietnam", JP: "Japan", KR: "South Korea", CN: "China", HK: "Hong Kong",
  TW: "Taiwan",
};

function flagEmoji(cc) {
  if (!cc || cc.length !== 2) return "";
  try {
    const A = 0x1F1E6;
    return String.fromCodePoint(A + (cc.charCodeAt(0) - 65)) +
           String.fromCodePoint(A + (cc.charCodeAt(1) - 65));
  } catch { return ""; }
}

function renderCountry(code) {
  const cc = (code || "").toUpperCase();
  if (!cc) return "(unknown)";
  const name = COUNTRY_NAMES[cc] || cc;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span style={{ fontSize: 14 }}>{flagEmoji(cc)}</span>
      <span>{name}</span>
      <span style={{ fontSize: 10, color: "var(--muted)", letterSpacing: 1 }}>{cc}</span>
    </span>
  );
}

export default function AnalyticsPanel() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/api/admin/analytics/summary?days=${days}`);
      setData(data);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const t = data?.totals || {};

  return (
    <div style={{ marginBottom: 28 }} data-testid="analytics-panel">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 10 }}>
        <h2 style={{ fontFamily: "var(--font-display)", fontSize: 20, color: "var(--white)", margin: 0, display: "inline-flex", alignItems: "center", gap: 8 }}>
          <BarChart3 size={16} style={{ color: "var(--gold)" }} />
          Traffic & Tool Analytics
        </h2>
        <div style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
          <div style={{ display: "inline-flex", borderRadius: 6, overflow: "hidden", border: "1px solid var(--line)" }} data-testid="analytics-window-toggle">
            {WINDOWS.map((w) => (
              <button
                key={w.days}
                onClick={() => setDays(w.days)}
                data-testid={`analytics-win-${w.label}`}
                style={{
                  padding: "6px 12px", fontSize: 11, letterSpacing: 1.5, textTransform: "uppercase",
                  background: days === w.days ? "var(--gold)" : "transparent",
                  color: days === w.days ? "var(--black)" : "var(--mid)",
                  border: "none", cursor: "pointer", fontFamily: "var(--font-mono)",
                }}
              >
                {w.label}
              </button>
            ))}
          </div>
          <button onClick={load} className="vbs-btn vbs-btn-ghost" style={{ padding: "6px 10px", fontSize: 11 }} data-testid="analytics-refresh">
            {loading ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
          </button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px,1fr))", gap: 12, marginBottom: 14 }}>
        <StatCard testid="stat-views-24h" label="Views · 24h" value={t.views_24h ?? 0} sub={`${t.visitors_24h ?? 0} unique visitors`} icon={Eye} />
        <StatCard testid="stat-views-7d" label="Views · 7d" value={t.views_7d ?? 0} icon={Eye} />
        <StatCard testid="stat-views-window" label={`Views · ${days}d`} value={t.views_window ?? 0} sub={`${t.visitors_window ?? 0} unique visitors`} icon={Users} />
        <StatCard testid="stat-tool-launches" label={`Tool launches · ${days}d`} value={t.tool_launches_window ?? 0} sub={`${t.tool_launches_24h ?? 0} in last 24h`} icon={Rocket} />
      </div>

      <div className="vbs-card" style={{ padding: 16, marginBottom: 14 }} data-testid="analytics-chart-card">
        <div style={{ fontSize: 11, color: "var(--gold-dim)", letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 8 }}>
          Daily pageviews · last {days} days
        </div>
        <DailyChart data={data?.daily || []} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px,1fr))", gap: 12 }}>
        <TopTable title={`Top countries (last ${days}d)`} rows={data?.top_countries} keyLabel="Country" testid="analytics-top-countries" renderKey={renderCountry} />
        <TopTable title={`Top pages (last ${days}d)`} rows={data?.top_pages} keyLabel="Path" testid="analytics-top-pages" />
        <TopTable title={`Top tools launched (last ${days}d)`} rows={data?.top_tools} keyLabel="Tool" testid="analytics-top-tools" />
        <TopTable title={`Top referrers (last ${days}d)`} rows={data?.top_referrers} keyLabel="Referrer" testid="analytics-top-referrers" />
        <TopTable title={`Top UTM sources (last ${days}d)`} rows={data?.top_utm_sources} keyLabel="utm_source" testid="analytics-top-utm-sources" />
        <TopTable title={`Top UTM campaigns (last ${days}d)`} rows={data?.top_utm_campaigns} keyLabel="utm_campaign" testid="analytics-top-utm-campaigns" />
      </div>
    </div>
  );
}
